import { Component, OnInit, Input, SimpleChanges, OnChanges } from '@angular/core';
import { AnalyticsService } from '@shared/services/analytics.service';
import { LoadState } from '../loader-directive/loader.module';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { Course } from '@shared/models/course.model';
import { ContentLineData } from './contents-stat-item/contents-stat-item.component';
import { Annotation, AnnotationType, Scene, SceneType } from '@shared/models';

function isAnnotationAI(annotation: Annotation) {
  return annotation.type === AnnotationType.npc || (annotation.label.endsWith('_ia') || annotation.label.endsWith('_ai'));
}
@Component({
  selector: 'content-stats',
  templateUrl: './content-stats.component.html',
  styleUrls: ['./content-stats.component.scss']
})
export class ContentStatComponent implements OnInit, OnChanges {
  @Input() experienceId: string;
  course: Course;
  @Input() date_from: Date;
  @Input() date_to: Date;
  loadStatus: string;
  contentStats: {
    xpStats: {
      _id: number;
      sessionNumber: number;
      uniqueUsers: number;
    },
    sceneStats: {
      _id: string,
      duration: number;
      activity: string,
      scene_id: string,
      users: number
    }[]
  }
  constructor(
    private _analyticsService: AnalyticsService,
    private _courseAPIService: CoursesAPIService
  ) {

  }

  async ngOnInit(): Promise<void> {
    await this.initOutline();
    await this.updateStats();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue)
      || (changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue)) {
      this.updateStats();
    }
  }
  async initOutline(): Promise<void> {
    this.loadStatus = LoadState.loading;
    this.course = await this._courseAPIService.getCourseView(this.experienceId).toPromise();
  }
  async updateStats(): Promise<void> {
    this.loadStatus = LoadState.loading;
    this.contentStats = await this._analyticsService.getContentsStats(this.experienceId, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
    this.loadStatus = LoadState.completed;
  }

  findScene(scene_id: string): Scene | null {
    for (const section of this.course.sections) {
      for (const scene of section.scenes) {
        if (scene_id === scene.id) {
          return scene;
        }
      }
    }
    return null;
  }
  get3DRoomData(): ContentLineData {
    if (!this.course) return;
    const { durationAvg, usersNum } = this.getRoom3DStats();
    const xpUsers = this.contentStats.xpStats?.uniqueUsers;
    return {
      title: this.course.course_model_3D ? this.course.course_model_3D.name : this.course.course_skybox.name,
      thumbnail: this.course.course_model_3D ? this.course.course_model_3D.thumbnail : this.course.course_skybox.thumbnail,
      tag: 'Room 3D',
      quizNum: this.countQuizzes((this.course as any).annotations),
      annotationNum: this.countInteractiveAnnotations((this.course as any).annotations),
      durationAvg: durationAvg,
      usersNum: usersNum,
      userPercent: Math.min(Math.floor(usersNum / xpUsers * 100), 100)
    };
  }
  getRoom3DStats(): { durationAvg: number, usersNum: number } {
    let room3DUserCount = 0;
    let room3DdurationAvg = 0;

    // Room 3D user count is the max userCount of 2D scenes, room3D average duration is the sum of duration of 2D scenes
    for (const section of this.course.sections) {
      for (const scene of section.scenes) {
        if (scene.type === SceneType.i2D || scene.type === SceneType.v2D) {
          const sceneStats = this.contentStats.sceneStats?.find(s => s.scene_id === scene.id);
          const sceneUserCount = sceneStats && sceneStats.users || 0;
          const sceneDurationAvg = sceneStats && sceneStats.duration || 0
          room3DUserCount = sceneUserCount > room3DUserCount ? sceneUserCount : room3DUserCount;
          room3DdurationAvg += sceneDurationAvg;
        }
      }
    }
    return {
      durationAvg: room3DdurationAvg,
      usersNum: room3DUserCount
    }
  }
  getRoom3DUsersCount(): number {
    const { usersNum } = this.getRoom3DStats();
    return usersNum
  }

  getRoom3DAnnotations(): Annotation[] {
    return (this.course as any).annotations.filter((annotation: Annotation) => {
      return this.isQuiz(annotation) || this.isInteractiveAnnotation(annotation);
    }).sort(this.sortAnnotations);
  }

  getSceneData(scene_id: string): ContentLineData {
    if (!this.course) return;
    const scene = this.findScene(scene_id);

    const xpUsers = this.contentStats.xpStats?.uniqueUsers;
    const sceneStats = this.contentStats.sceneStats?.find(s => s.scene_id === scene_id);

    return {
      title: scene.title,
      thumbnail: scene.media?.thumbnail,
      tag: (scene.type === SceneType.v360 || scene.type === SceneType.i360) ? '360 Scene' : '2D Scene',
      quizNum: this.countQuizzes(scene.annotations),
      annotationNum: this.countInteractiveAnnotations(scene.annotations),
      durationAvg: sceneStats && sceneStats.duration,
      usersNum: sceneStats && sceneStats.users,
      userPercent: sceneStats && Math.min(Math.floor(sceneStats.users / xpUsers * 100), 100)
    }

  }
  getSceneViewCount(scene_id: string): number {
    const sceneStats = this.contentStats.sceneStats?.find(s => s.scene_id === scene_id);
    return sceneStats && sceneStats.users;
  }
  getSceneAnnotations(scene_id: string): Annotation[] {
    const scene = this.findScene(scene_id);
    return scene.annotations.filter((annotation) => {
      return this.isQuiz(annotation) || this.isInteractiveAnnotation(annotation);
    }).sort(this.sortAnnotations)
  }
  isQuiz(annotation: Annotation): boolean {
    return annotation.type === AnnotationType.quiz
  }
  isInteractiveAnnotation(annotation: Annotation): boolean {
    return (annotation.properties.action && annotation.properties.action.effects && annotation.properties.action.effects.length > 0) || isAnnotationAI(annotation);
  }

  countQuizzes(annotations: Annotation[]) {
    return annotations.filter(this.isQuiz).length;
  }
  countInteractiveAnnotations(annotations: Annotation[]) {
    return annotations.filter(this.isInteractiveAnnotation).length;
  }
  sortAnnotations(annotation1: Annotation, annotation2: Annotation) {
    if (annotation1.type > annotation2.type) return 1;
    if (annotation1.type < annotation2.type) return -1;
    return annotation1.label.localeCompare(annotation2.label);
  }
}
